import { Link, usePage, router } from "@inertiajs/react"
import { useState, useCallback, useEffect, useRef, type ReactNode } from "react"
import { Toaster } from "@/components/ui/sonner"
import FlashMessages from "@/components/flash-messages"
import ImpersonationBanner from "@/components/impersonation-banner"
import type { SharedProps } from "@/types"
import { cn } from "@/lib/utils"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

type NavItem = { href: string; label: string; icon: React.ComponentType<{ className?: string }> }
type NavSection = { heading: string; items: NavItem[] }

const NAV_SECTIONS: NavSection[] = [
  {
    heading: "",
    items: [
      { href: "/admin", label: "Overview", icon: ChartIcon },
    ],
  },
  {
    heading: "Content",
    items: [
      { href: "/admin/listings", label: "Listings", icon: BuildingIcon },
      { href: "/admin/videos", label: "Videos", icon: VideoIcon },
      { href: "/admin/posts", label: "Posts", icon: PostsIcon },
    ],
  },
  {
    heading: "Customers",
    items: [
      { href: "/admin/accounts", label: "Accounts", icon: AccountsIcon },
      { href: "/admin/users", label: "Users", icon: UsersIcon },
    ],
  },
  {
    heading: "Platform",
    items: [
      { href: "/admin/jobs", label: "Jobs", icon: QueueIcon },
      { href: "/admin/costs", label: "Costs", icon: CostsIcon },
      { href: "/admin/cost-rates", label: "Rates", icon: RatesIcon },
      { href: "/admin/plans", label: "Plans", icon: PlansIcon },
      { href: "/admin/settings", label: "Settings", icon: SettingsIcon },
    ],
  },
]

const COLLAPSED_KEY = "sidebar-collapsed"

export default function AdminLayout({ children }: { children: ReactNode }) {
  const { url } = usePage()
  const { impersonating } = usePage<SharedProps>().props
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false
    return localStorage.getItem(COLLAPSED_KEY) === "true"
  })
  const [hovered, setHovered] = useState(false)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const closeMobile = useCallback(() => setMobileOpen(false), [])

  useEffect(() => {
    return router.on("navigate", closeMobile)
  }, [closeMobile])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev
      localStorage.setItem(COLLAPSED_KEY, String(next))
      if (next) setHovered(false)
      return next
    })
  }, [])

  const handleMouseEnter = useCallback(() => {
    if (!collapsed) return
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    hoverTimeoutRef.current = setTimeout(() => setHovered(true), 80)
  }, [collapsed])

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    setHovered(false)
  }, [])

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current)
    }
  }, [])

  const expanded = !collapsed || hovered

  return (
    <TooltipProvider delayDuration={0}>
      <div className={`dark flex min-h-screen bg-charcoal/95 ${impersonating ? "pt-10" : ""}`}>
        <ImpersonationBanner />

        {/* Desktop sidebar spacer */}
        <div
          className={cn(
            "hidden lg:block shrink-0 transition-[width] duration-300 ease-in-out",
            collapsed ? "w-[72px]" : "w-64",
          )}
        />

        {/* Desktop sidebar */}
        <aside
          className={cn(
            "hidden lg:flex fixed z-30 flex-col border-r border-white/10 bg-charcoal transition-all duration-300 ease-in-out",
            impersonating ? "top-10 h-[calc(100vh-2.5rem)]" : "top-0 h-screen",
            expanded ? "w-64" : "w-[72px]",
            collapsed && hovered && "shadow-xl shadow-black/30",
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="flex h-full flex-col overflow-hidden">
            {/* Logo + toggle */}
            <div className={cn(
              "flex h-16 items-center border-b border-white/10 transition-[padding] duration-300",
              expanded ? "px-5" : "px-3",
            )}>
              {expanded ? (
                <Link href="/admin" className="flex items-baseline text-xl font-display font-semibold tracking-tight whitespace-nowrap">
                  <span className="text-cream">Quite</span>
                  <span className="text-red">Right.</span>
                </Link>
              ) : (
                <Link href="/admin" className="flex items-baseline text-lg font-display font-semibold tracking-tight mx-auto">
                  <span className="text-cream">Q</span>
                  <span className="text-red">.</span>
                </Link>
              )}
              <button
                onClick={toggleCollapsed}
                className={cn(
                  "flex size-7 items-center justify-center rounded-md text-cream/40 hover:bg-white/10 hover:text-cream transition-all duration-200",
                  expanded ? "ml-auto opacity-100" : "absolute -right-3 top-5 size-6 rounded-full bg-charcoal border border-white/10 shadow-sm opacity-0 group-hover/sidebar:opacity-100 hover:bg-white/10",
                )}
                aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              >
                <ToggleIcon className="size-3.5" collapsed={collapsed} />
              </button>
            </div>

            {/* Nav links */}
            <nav className={cn(
              "flex-1 overflow-y-auto overflow-x-hidden py-4 transition-[padding] duration-300",
              expanded ? "px-3" : "px-2",
            )}>
              <div className="space-y-5">
                {NAV_SECTIONS.map((section) => (
                  <div key={section.heading || "top"}>
                    {section.heading && expanded && (
                      <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-widest text-cream/30">
                        {section.heading}
                      </p>
                    )}
                    {section.heading && !expanded && (
                      <div className="mx-auto mb-1.5 h-px w-6 bg-white/10" />
                    )}
                    <div className="space-y-0.5">
                      {section.items.map((item) => {
                        const active = item.href === "/admin"
                          ? url === "/admin"
                          : url.startsWith(item.href)
                        const Icon = item.icon
                        const link = (
                          <Link
                            key={item.href}
                            href={item.href}
                            className={cn(
                              "flex items-center rounded-lg text-sm font-medium transition-all duration-200",
                              expanded ? "gap-3 px-3 py-2" : "justify-center px-2 py-2",
                              active
                                ? "bg-white/10 text-cream"
                                : "text-cream/50 hover:bg-white/5 hover:text-cream",
                            )}
                          >
                            <Icon className="size-4.5 shrink-0" />
                            {expanded && (
                              <span className="whitespace-nowrap animate-in fade-in duration-200">
                                {item.label}
                              </span>
                            )}
                          </Link>
                        )

                        if (!expanded) {
                          return (
                            <Tooltip key={item.href}>
                              <TooltipTrigger asChild>{link}</TooltipTrigger>
                              <TooltipContent side="right" sideOffset={8}>
                                {item.label}
                              </TooltipContent>
                            </Tooltip>
                          )
                        }

                        return link
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </nav>

            {/* Back to dashboard */}
            <div className="border-t border-white/10 p-3">
              {expanded ? (
                <Link
                  href="/dashboard"
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-cream/50 transition-colors hover:bg-white/5 hover:text-cream"
                >
                  <ArrowLeftIcon className="size-5 shrink-0" />
                  <span className="whitespace-nowrap animate-in fade-in duration-200">
                    Back to dashboard
                  </span>
                </Link>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      href="/dashboard"
                      className="flex items-center justify-center rounded-lg px-2 py-2.5 text-sm font-medium text-cream/50 transition-colors hover:bg-white/5 hover:text-cream"
                    >
                      <ArrowLeftIcon className="size-5 shrink-0" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>
                    Back to dashboard
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-16 items-center justify-between border-b border-white/10 bg-charcoal px-4 lg:hidden">
            <Link href="/admin" className="flex items-baseline text-xl font-display font-semibold tracking-tight">
              <span className="text-cream">Quite</span>
              <span className="text-red">Right.</span>
            </Link>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="flex size-10 items-center justify-center rounded-full text-cream hover:bg-white/10"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
            >
              {mobileOpen ? (
                <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="size-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                </svg>
              )}
            </button>
          </header>

          {/* Mobile nav overlay */}
          {mobileOpen && (
            <div className="border-b border-white/10 bg-charcoal p-4 lg:hidden">
              <nav className="space-y-4">
                {NAV_SECTIONS.map((section) => (
                  <div key={section.heading || "top"}>
                    {section.heading && (
                      <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-widest text-cream/30">
                        {section.heading}
                      </p>
                    )}
                    <div className="space-y-0.5">
                      {section.items.map((item) => {
                        const active = item.href === "/admin"
                          ? url === "/admin"
                          : url.startsWith(item.href)
                        const Icon = item.icon
                        return (
                          <Link
                            key={item.href}
                            href={item.href}
                            onClick={closeMobile}
                            className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                              active ? "bg-white/10 text-cream" : "text-cream/50 hover:bg-white/5"
                            }`}
                          >
                            <Icon className="size-5" />
                            {item.label}
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </nav>
              <div className="mt-4 border-t border-white/10 pt-4">
                <Link
                  href="/dashboard"
                  onClick={closeMobile}
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-cream/50 hover:bg-white/5 hover:text-cream"
                >
                  <ArrowLeftIcon className="size-5" />
                  Back to dashboard
                </Link>
              </div>
            </div>
          )}

          {/* Main content */}
          <main className="min-w-0 flex-1 overflow-x-hidden px-4 py-8 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl">{children}</div>
          </main>
        </div>

        <FlashMessages />
        <Toaster position="top-right" richColors />
      </div>
    </TooltipProvider>
  )
}

function ToggleIcon({ className, collapsed }: { className?: string; collapsed: boolean }) {
  return (
    <svg
      className={cn(className, "transition-transform duration-300", collapsed && "rotate-180")}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
    </svg>
  )
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  )
}

function QueueIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
    </svg>
  )
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z" />
    </svg>
  )
}

function VideoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
    </svg>
  )
}

function AccountsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
    </svg>
  )
}

function CostsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0 1 15.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 0 1 3 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 0 0-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 0 1-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 0 0 3 15h-.75M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm3 0h.008v.008H18V10.5Zm-12 0h.008v.008H6V10.5Z" />
    </svg>
  )
}

function RatesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6Z" />
    </svg>
  )
}

function PlansIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
    </svg>
  )
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  )
}

function PostsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
    </svg>
  )
}

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
    </svg>
  )
}
